/**
 * Platform Drift Page — Phase 3 observability dashboard.
 * Read-only. Shows drift between legacy (tenant_modules + module_entitlements)
 * and canonical (tenant_app_assignments) for each tenant.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../auth/AdminAuthContext';
import { PermissionGate } from '../components';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../../components/ui/table';
import { RefreshCw, GitCompare, AlertTriangle, Building2, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PlatformDriftPage = () => {
  const { getAdminToken } = useAdminAuth();
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  // THE DEFECT THIS CLOSES: both fetches failed silently (the backend for
  // this page does not exist — the /api/admin/platform-drift endpoints 404), so
  // `summary` stayed null, every card optional-chained to an em-dash, and
  // the row list fell through to "No drift detected". A page whose own
  // header says "Zero is the healthy number" rendered a total backend
  // absence as a clean bill of health. A failed measurement is not a
  // measurement of zero.
  const [unavailable, setUnavailable] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [filters, setFilters] = useState({
    tenant_id: '',
    app_code: '',
    drift_type: '',
  });

  const loadSummary = useCallback(async () => {
    try {
      const t = getAdminToken();
      const headers = t ? { Authorization: `Bearer ${t}` } : {};
      const res = await axios.get(`${API_URL}/api/admin/platform-drift/summary`, { headers });
      setSummary(res.data);
      setUnavailable(null);
    } catch (e) {
      console.error('drift summary failed', e);
      setSummary(null);
      setUnavailable(
        e?.response?.status === 404
          ? 'Drift monitoring is not implemented on this deployment — the reporting endpoint does not exist.'
          : `Drift could not be measured (${e?.response?.status || 'network error'}).`
      );
    }
  }, [getAdminToken]);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const t = getAdminToken();
      const headers = t ? { Authorization: `Bearer ${t}` } : {};
      const params = new URLSearchParams();
      if (filters.tenant_id) params.set('tenant_id', filters.tenant_id);
      if (filters.app_code) params.set('app_code', filters.app_code);
      if (filters.drift_type) params.set('drift_type', filters.drift_type);
      params.set('limit', '500');
      const res = await axios.get(`${API_URL}/api/admin/platform-drift?${params.toString()}`, { headers });
      setRows(res.data.rows || []);
    } catch (e) {
      console.error('drift list failed', e);
      setRows([]);   // rendered as UNKNOWN, not as "none", because `unavailable` is set
      setUnavailable((prev) => prev || 'Drift rows could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [filters, getAdminToken]);

  useEffect(() => {
    loadSummary();
    loadRows();
  }, [loadSummary, loadRows]);

  const handleReconcile = async () => {
    setReconciling(true);
    try {
      const t = getAdminToken();
      const headers = t ? { Authorization: `Bearer ${t}` } : {};
      await axios.post(`${API_URL}/api/admin/platform-drift/reconcile-now`, {}, { headers });
      await loadSummary();
      await loadRows();
    } catch (e) {
      console.error('reconcile failed', e);
      setUnavailable(
        e?.response?.status === 404
          ? 'Reconcile is not available — drift monitoring is not implemented on this deployment.'
          : `Reconcile failed (${e?.response?.status || 'network error'}).`
      );
    } finally {
      setReconciling(false);
    }
  };

  const handleClearFilters = () => {
    setFilters({ tenant_id: '', app_code: '', drift_type: '' });
  };

  const driftBadge = (type) => {
    if (type === 'legacy_only') {
      return <Badge variant="destructive" data-testid={`drift-badge-legacy-${type}`}>legacy_only (missing in canonical)</Badge>;
    }
    if (type === 'canonical_only') {
      return <Badge className="bg-amber-500" data-testid={`drift-badge-canonical-${type}`}>canonical_only (missing in legacy)</Badge>;
    }
    return <Badge>{type}</Badge>;
  };

  return (
    <div className="p-6 space-y-6" data-testid="platform-drift-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <GitCompare className="h-6 w-6" />
            Platform Drift
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Entitlement is recorded in two places while the platform finishes a
            migration: the older <code>tenant_modules</code> and the canonical
            <code> tenant_app_assignments</code>. This lists tenants where the two
            disagree.
          </p>
          <p className="text-sm text-gray-500 mt-1">
            <span className="font-medium text-gray-700">Zero is the healthy number.</span>{' '}
            A tenant appearing here may see a module it should not, or lose one it
            should have, depending on which record a given screen reads. Reconcile
            writes the canonical record from the legacy one. This page becomes
            unnecessary once the legacy collections are retired.
          </p>
        </div>
        <PermissionGate permission="provisioning:execute">
          <Button
            onClick={handleReconcile}
            disabled={reconciling || !!unavailable}
            title={unavailable ? 'Unavailable — drift monitoring is not implemented on this deployment' : undefined}
            variant="outline"
            data-testid="drift-reconcile-now-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${reconciling ? 'animate-spin' : ''}`} />
            Reconcile Now
          </Button>
        </PermissionGate>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="drift-summary-cards">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total drift rows</CardDescription>
            <CardTitle className="text-3xl" data-testid="drift-total-rows">
              {unavailable ? <span className="text-base text-gray-400">Not measured</span> : (summary?.total_drift_rows ?? '—')}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Tenants affected</CardDescription>
            <CardTitle className="text-3xl" data-testid="drift-tenants-affected">
              {unavailable ? <span className="text-base text-gray-400">Not measured</span> : (summary?.tenants_affected ?? '—')}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Top drifting app</CardDescription>
            <CardTitle className="text-xl" data-testid="drift-top-app">
              {unavailable ? <span className="text-base text-gray-400">Not measured</span> : (summary?.top_apps?.[0]?.app_code ?? '—')}
              {summary?.top_apps?.[0] && (
                <span className="text-sm text-gray-500 ml-2">×{summary.top_apps[0].count}</span>
              )}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Last reconcile run</CardDescription>
            <CardTitle className="text-sm flex items-center gap-1" data-testid="drift-latest-run">
              <Clock className="h-4 w-4" />
              {unavailable ? 'Not measured' : (summary?.latest_run_at ? new Date(summary.latest_run_at).toLocaleString() : '—')}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Input
            placeholder="Tenant ID"
            value={filters.tenant_id}
            onChange={(e) => setFilters({ ...filters, tenant_id: e.target.value })}
            data-testid="drift-filter-tenant-id"
          />
          <Input
            placeholder="App code (e.g. docflow)"
            value={filters.app_code}
            onChange={(e) => setFilters({ ...filters, app_code: e.target.value })}
            data-testid="drift-filter-app-code"
          />
          <Select
            value={filters.drift_type || 'all'}
            onValueChange={(v) => setFilters({ ...filters, drift_type: v === 'all' ? '' : v })}
          >
            <SelectTrigger data-testid="drift-filter-type">
              <SelectValue placeholder="All drift types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="legacy_only">legacy_only</SelectItem>
              <SelectItem value="canonical_only">canonical_only</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex gap-2">
            <Button onClick={loadRows} disabled={loading} data-testid="drift-apply-filters-btn">
              {loading ? 'Loading…' : 'Apply'}
            </Button>
            <Button variant="outline" onClick={handleClearFilters} data-testid="drift-clear-filters-btn">
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Rows */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Drift rows ({rows.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {rows.length === 0 && !loading && unavailable && (
            <div className="text-center py-10" data-testid="drift-unavailable">
              <p className="text-sm font-medium text-amber-700">Drift status unavailable</p>
              <p className="text-sm text-gray-500 mt-1">{unavailable}</p>
              <p className="text-xs text-gray-400 mt-2">
                This is not a report of zero drift. Nothing was measured.
              </p>
            </div>
          )}
          {rows.length === 0 && !loading && !unavailable && (
            <div className="text-center text-sm text-gray-500 py-10" data-testid="drift-empty">
              No drift detected — all tenants are in sync as of the last reconcile run.
            </div>
          )}
          {rows.length > 0 && (
            <Table data-testid="drift-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Tenant</TableHead>
                  <TableHead>App</TableHead>
                  <TableHead>Drift type</TableHead>
                  <TableHead>Detected at</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r, idx) => (
                  <TableRow key={`${r.tenant_id}-${r.app_code}-${r.drift_type}-${idx}`} data-testid="drift-row">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-gray-400" />
                        <div>
                          <div className="font-medium">{r.tenant_name || '—'}</div>
                          <div className="text-xs text-gray-500 font-mono">{r.tenant_id}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="text-sm bg-gray-100 px-1.5 py-0.5 rounded">{r.app_code}</code>
                    </TableCell>
                    <TableCell>{driftBadge(r.drift_type)}</TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {r.detected_at ? new Date(r.detected_at).toLocaleString() : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PlatformDriftPage;
